import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Text } from '@feathr/components';
import {
  formatImportCount,
  getPartialImportCounts,
} from '@feathr/extender/components/ImportRunLines';

import { hasRecordedCounts } from './EventImportCard.utils';

interface IPartnerImportCountsLineProps {
  importer: Importer;
}

/** What a partner import wrote: how far a failed run got, or what a complete run did. */
function PartnerImportCountsLine({
  importer,
}: Readonly<IPartnerImportCountsLineProps>): JSX.Element | null {
  const { t } = useTranslation();

  // Placeholder copy, pending Product and Design (decision 23).
  const partialCounts =
    importer.get('state') === 'failed' ? getPartialImportCounts(importer) : undefined;
  if (partialCounts) {
    return (
      <Text.DefaultRegular truncate={false}>
        {t('Imported {{imported}} of {{formattedTotal}} rows before the import stopped.', {
          count: partialCounts.total,
          formattedTotal: formatImportCount(partialCounts.total),
          imported: formatImportCount(partialCounts.imported),
        })}
      </Text.DefaultRegular>
    );
  }

  if (!hasRecordedCounts(importer)) {
    return null;
  }

  const inserted = importer.get('inserted', 0);
  const duplicates = importer.get('duplicates', 0);
  const skipped = importer.get('skipped', 0);

  return (
    <Stack gap={0}>
      <Text.DefaultRegular truncate={false}>
        {t('Added {{formattedCount}} new partners.', {
          count: inserted,
          formattedCount: formatImportCount(inserted),
        })}
      </Text.DefaultRegular>
      <Text.DefaultRegular truncate={false}>
        {t('Updated {{formattedCount}} existing partners.', {
          count: duplicates,
          formattedCount: formatImportCount(duplicates),
        })}
      </Text.DefaultRegular>
      <Text.DefaultRegular truncate={false}>
        {t("{{formattedCount}} rows weren't imported.", {
          count: skipped,
          formattedCount: formatImportCount(skipped),
        })}
      </Text.DefaultRegular>
    </Stack>
  );
}

export default observer(PartnerImportCountsLine);
